import { useState, useRef, useEffect, useCallback } from "react";

import { View, Text, Button } from "../components/base";
import { Icon } from "../components/base/Icon";
import { PageLayout } from "../components/PageLayout";
import { API_URL, TRANSCRIBE_WS_URL } from "../config";
import { useSTTProviders, STTModel, STTModelCapabilities } from "../hooks/useSTTProviders";

// --- Types ---

type TranscribeEvent = {
  id: number;
  timestamp: Date;
  type: string;
  text?: string;
  latencyMs?: number;
  raw: Record<string, unknown>;
};

type ConnectionStatus = "disconnected" | "connecting" | "connected" | "listening";

type PanelRef = {
  ws: WebSocket | null;
  speechStopTime: number | null;
  latencySamples: number[];
  eventId: number;
};

type PanelConfig = {
  modelValue: string;
  language: string;
  prompt: string;
};

// --- Audio helpers ---

const SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = 320; // 20ms at 16kHz

const LANGUAGE_LABELS: Record<string, string> = {
  auto: "Auto-Detect",
  multi: "Auto-Detect",
  af: "Afrikaans",
  am: "Amharic",
  ar: "Arabic",
  as: "Assamese",
  az: "Azerbaijani",
  ba: "Bashkir",
  be: "Belarusian",
  bg: "Bulgarian",
  bn: "Bengali",
  bo: "Tibetan",
  br: "Breton",
  bs: "Bosnian",
  ca: "Catalan",
  cs: "Czech",
  cy: "Welsh",
  da: "Danish",
  de: "German",
  el: "Greek",
  en: "English",
  es: "Spanish",
  et: "Estonian",
  eu: "Basque",
  fa: "Persian",
  fi: "Finnish",
  fil: "Filipino",
  fo: "Faroese",
  fr: "French",
  gl: "Galician",
  gu: "Gujarati",
  ha: "Hausa",
  haw: "Hawaiian",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  ht: "Haitian Creole",
  hu: "Hungarian",
  hy: "Armenian",
  id: "Indonesian",
  is: "Icelandic",
  it: "Italian",
  ja: "Japanese",
  jw: "Javanese",
  ka: "Georgian",
  kk: "Kazakh",
  km: "Khmer",
  kn: "Kannada",
  ko: "Korean",
  la: "Latin",
  lb: "Luxembourgish",
  ln: "Lingala",
  lo: "Lao",
  lt: "Lithuanian",
  lv: "Latvian",
  mg: "Malagasy",
  mi: "Maori",
  mk: "Macedonian",
  ml: "Malayalam",
  mn: "Mongolian",
  mr: "Marathi",
  ms: "Malay",
  mt: "Maltese",
  my: "Burmese",
  ne: "Nepali",
  nl: "Dutch",
  nn: "Norwegian Nynorsk",
  no: "Norwegian",
  oc: "Occitan",
  pa: "Punjabi",
  pl: "Polish",
  ps: "Pashto",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  sa: "Sanskrit",
  sd: "Sindhi",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  sn: "Shona",
  so: "Somali",
  sq: "Albanian",
  sr: "Serbian",
  su: "Sundanese",
  sv: "Swedish",
  sw: "Swahili",
  ta: "Tamil",
  te: "Telugu",
  tg: "Tajik",
  th: "Thai",
  tk: "Turkmen",
  tl: "Tagalog",
  tr: "Turkish",
  tt: "Tatar",
  uk: "Ukrainian",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  yi: "Yiddish",
  yo: "Yoruba",
  yue: "Cantonese",
  zh: "Chinese",
};

function float32ToInt16Base64(float32: Float32Array): string {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function fetchWsToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_URL}/v1/auth/ws-token`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    return data.token || null;
  } catch {
    return null;
  }
}

function makePanelRef(): PanelRef {
  return { ws: null, speechStopTime: null, latencySamples: [], eventId: 0 };
}

function promptLabel(caps: STTModelCapabilities | undefined): string | null {
  if (!caps?.supportsPrompt) return null;
  if (caps.promptType === "keywords") return "Keywords";
  if (caps.promptType === "system") return "Context Prompt";
  return "Prompt";
}

function promptPlaceholder(caps: STTModelCapabilities | undefined): string {
  if (caps?.promptType === "keywords") return "";
  return "";
}

// --- PanelConfigUI sub-component ---

function PanelConfigUI({
  side,
  config,
  onChange,
  allModels,
  getModelByValue,
  getProviderForModel,
  disabled,
}: {
  side: "A" | "B";
  config: PanelConfig;
  onChange: (patch: Partial<PanelConfig>) => void;
  allModels: STTModel[];
  getModelByValue: (v: string) => STTModel | undefined;
  getProviderForModel: (v: string) => { id: string } | undefined;
  disabled: boolean;
}) {
  const model = getModelByValue(config.modelValue);
  const caps = model?.capabilities;
  const provider = getProviderForModel(config.modelValue);
  const languages = caps?.languages ?? ["en"];
  const pLabel = promptLabel(caps);

  return (
    <View className="flex flex-col gap-2 flex-1 min-w-0">
      <Text className="text-xs font-semibold text-gray-500 dark:text-gray-400">Model {side}</Text>
      <select
        value={config.modelValue}
        onChange={(e) => {
          const newModel = getModelByValue(e.target.value);
          const newLangs = newModel?.capabilities.languages ?? ["en"];
          // Reset language to first available if current isn't supported
          const langValid = newLangs.includes(config.language);
          onChange({
            modelValue: e.target.value,
            ...(!langValid && { language: newLangs[0] || "en" }),
            // Clear prompt if new model doesn't support it
            ...(!newModel?.capabilities.supportsPrompt && { prompt: "" }),
          });
        }}
        disabled={disabled}
        className="h-9 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50"
      >
        {allModels.map((m) => {
          const p = getProviderForModel(m.value);
          return (
            <option
              key={m.value}
              value={m.value}
            >
              {m.label} ({p?.id ?? ""})
            </option>
          );
        })}
      </select>

      <View className="flex gap-2">
        {/* Language */}
        <View className="flex flex-col gap-1 flex-1 min-w-0">
          <Text className="text-xs text-gray-500 dark:text-gray-400">Language</Text>
          <select
            value={config.language}
            onChange={(e) => onChange({ language: e.target.value })}
            disabled={disabled}
            className="h-8 px-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            {languages.map((lang) => (
              <option
                key={lang}
                value={lang}
              >
                {LANGUAGE_LABELS[lang] || lang}
              </option>
            ))}
          </select>
        </View>

        {/* Prompt / Keywords (if supported) */}
        {pLabel && (
          <View className="flex flex-col gap-1 flex-[2] min-w-0">
            <Text className="text-xs text-gray-500 dark:text-gray-400">{pLabel}</Text>
            <input
              type="text"
              value={config.prompt}
              onChange={(e) => onChange({ prompt: e.target.value })}
              disabled={disabled}
              placeholder={promptPlaceholder(caps)}
              className="h-8 px-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-100 disabled:opacity-50 placeholder:text-gray-400"
            />
          </View>
        )}
      </View>

      {provider && (
        <Text className="text-[10px] text-gray-400 dark:text-gray-600 truncate">
          {provider.id} / {config.modelValue}
        </Text>
      )}
    </View>
  );
}

// --- TranscribePanel sub-component ---

function TranscribePanel({
  label,
  events,
  avgLatencyMs,
  sampleCount,
  showPartials,
}: {
  label: string;
  events: TranscribeEvent[];
  avgLatencyMs: number | null;
  sampleCount: number;
  showPartials: boolean;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const filtered = showPartials ? events : events.filter((e) => e.type !== "transcription.partial");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [filtered]);

  return (
    <View className="flex flex-col flex-1 min-w-0">
      <View className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 rounded-t-lg">
        <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</Text>
        {avgLatencyMs !== null && (
          <Text className="text-xs text-gray-500 dark:text-gray-400">
            Avg Turn Taking: <span className="font-semibold text-yellow-600 dark:text-yellow-400">{avgLatencyMs}ms</span>
            <span className="text-gray-400 dark:text-gray-500"> ({sampleCount})</span>
          </Text>
        )}
      </View>
      <View className="flex-1 min-h-[400px] max-h-[60vh] overflow-y-auto bg-gray-50 dark:bg-gray-900 p-3 font-mono text-sm rounded-b-lg border border-t-0 border-gray-200 dark:border-gray-700">
        {filtered.length === 0 && <Text className="text-gray-400 dark:text-gray-500 italic">Waiting for events...</Text>}
        {filtered.map((evt) => (
          <EventRow
            key={evt.id}
            event={evt}
          />
        ))}
        <div ref={endRef} />
      </View>
    </View>
  );
}

// --- Component ---

export const TranscribePage = () => {
  const { getAllModels, getModelByValue, getProviderForModel, loading } = useSTTProviders();
  const allModels = getAllModels();

  // Per-panel config
  const [configA, setConfigA] = useState<PanelConfig>({ modelValue: "flux-general-en", language: "en", prompt: "" });
  const [configB, setConfigB] = useState<PanelConfig>({ modelValue: "gpt-4o-transcribe", language: "en", prompt: "" });

  // Connection state
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [eventsA, setEventsA] = useState<TranscribeEvent[]>([]);
  const [eventsB, setEventsB] = useState<TranscribeEvent[]>([]);
  const [micOn, setMicOn] = useState(false);
  const [avgLatencyA, setAvgLatencyA] = useState<number | null>(null);
  const [avgLatencyB, setAvgLatencyB] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPartials, setShowPartials] = useState(true);

  // Per-panel refs
  const panelARef = useRef<PanelRef>(makePanelRef());
  const panelBRef = useRef<PanelRef>(makePanelRef());
  const readyCountRef = useRef(0);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const addEventA = useCallback((evt: Omit<TranscribeEvent, "id" | "timestamp">) => {
    const id = ++panelARef.current.eventId;
    setEventsA((prev) => [...prev, { ...evt, id, timestamp: new Date() }]);
  }, []);

  const addEventB = useCallback((evt: Omit<TranscribeEvent, "id" | "timestamp">) => {
    const id = ++panelBRef.current.eventId;
    setEventsB((prev) => [...prev, { ...evt, id, timestamp: new Date() }]);
  }, []);

  // --- Mic control (defined early so setupWs can reference it) ---

  const stopMic = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setMicOn(false);
    setStatus((prev) => (prev === "listening" ? "connected" : prev));
  }, []);

  // --- WebSocket setup for one panel ---

  const setupWs = useCallback(
    (
      ws: WebSocket,
      panelConfig: PanelConfig,
      providerId: string,
      panel: PanelRef,
      addEvent: (evt: Omit<TranscribeEvent, "id" | "timestamp">) => void,
      setAvgLatency: React.Dispatch<React.SetStateAction<number | null>>,
    ) => {
      ws.onmessage = (e) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(e.data as string);
        } catch {
          return;
        }

        const type = msg.type as string;

        if (type === "session.created") {
          const session: Record<string, unknown> = {
            model: panelConfig.modelValue,
            provider: providerId.toUpperCase(),
            language: panelConfig.language,
            input_audio_format: "pcm16",
            sample_rate: SAMPLE_RATE,
          };
          if (panelConfig.prompt) {
            session.prompt = panelConfig.prompt;
          }
          ws.send(JSON.stringify({ type: "session.update", session }));
          addEvent({ type, raw: msg });
          return;
        }

        if (type === "session.updated") {
          readyCountRef.current += 1;
          if (readyCountRef.current >= 2) {
            setStatus("connected");
          }
          addEvent({ type, raw: msg });
          return;
        }

        if (type === "speech.stopped") {
          panel.speechStopTime = performance.now();
        }

        let computedLatency: number | undefined;
        if (type === "transcription.completed" && panel.speechStopTime !== null) {
          computedLatency = Math.round(performance.now() - panel.speechStopTime);
          panel.latencySamples.push(computedLatency);
          const avg = Math.round(panel.latencySamples.reduce((a, b) => a + b, 0) / panel.latencySamples.length);
          setAvgLatency(avg);
          panel.speechStopTime = null;
        }

        const text = (msg.text as string) ?? ((msg.transcription as Record<string, unknown>)?.text as string) ?? undefined;

        addEvent({ type, text, latencyMs: computedLatency, raw: msg });
      };

      ws.onerror = () => {
        setError("WebSocket error");
        setStatus("disconnected");
      };

      ws.onclose = (ev) => {
        if (ev.code === 4001) {
          setError("Unauthorized — session may have expired");
        }
        panel.ws = null;
        setStatus("disconnected");
        stopMic();
      };
    },
    [stopMic],
  );

  // --- Connect / disconnect ---

  const connect = useCallback(async () => {
    if (!TRANSCRIBE_WS_URL) {
      setError("TRANSCRIBE_WS_URL not configured for this environment");
      return;
    }

    const token = await fetchWsToken();
    if (!token) {
      setError("Failed to get auth token — are you logged in?");
      return;
    }

    setError(null);
    setEventsA([]);
    setEventsB([]);
    setAvgLatencyA(null);
    setAvgLatencyB(null);
    readyCountRef.current = 0;
    panelARef.current = makePanelRef();
    panelBRef.current = makePanelRef();
    setStatus("connecting");

    const url = `${TRANSCRIBE_WS_URL}?token=${encodeURIComponent(token)}`;

    const wsA = new WebSocket(url);
    const wsB = new WebSocket(url);
    panelARef.current.ws = wsA;
    panelBRef.current.ws = wsB;

    const providerA = getProviderForModel(configA.modelValue);
    const providerB = getProviderForModel(configB.modelValue);

    setupWs(wsA, configA, providerA?.id ?? "", panelARef.current, addEventA, setAvgLatencyA);
    setupWs(wsB, configB, providerB?.id ?? "", panelBRef.current, addEventB, setAvgLatencyB);
  }, [configA, configB, addEventA, addEventB, setupWs, getProviderForModel]);

  const disconnect = useCallback(() => {
    for (const panel of [panelARef.current, panelBRef.current]) {
      const ws = panel.ws;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "session.close" }));
        ws.close();
      }
      panel.ws = null;
    }
    setStatus("disconnected");
    stopMic();
  }, [stopMic]);

  // --- Mic control ---

  const startMic = useCallback(async () => {
    const wsA = panelARef.current.ws;
    const wsB = panelBRef.current.ws;
    if ((!wsA || wsA.readyState !== WebSocket.OPEN) && (!wsB || wsB.readyState !== WebSocket.OPEN)) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);

      const sendAudioToBoth = (float32: Float32Array) => {
        const payload = JSON.stringify({
          type: "audio.append",
          audio: float32ToInt16Base64(float32),
        });
        const a = panelARef.current.ws;
        const b = panelBRef.current.ws;
        if (a && a.readyState === WebSocket.OPEN) a.send(payload);
        if (b && b.readyState === WebSocket.OPEN) b.send(payload);
      };

      try {
        const workletCode = `
          class PcmProcessor extends AudioWorkletProcessor {
            constructor() {
              super();
              this.buffer = new Float32Array(0);
            }
            process(inputs) {
              const input = inputs[0][0];
              if (!input) return true;
              const newBuf = new Float32Array(this.buffer.length + input.length);
              newBuf.set(this.buffer);
              newBuf.set(input, this.buffer.length);
              this.buffer = newBuf;
              while (this.buffer.length >= ${CHUNK_SAMPLES}) {
                const chunk = this.buffer.slice(0, ${CHUNK_SAMPLES});
                this.buffer = this.buffer.slice(${CHUNK_SAMPLES});
                this.port.postMessage(chunk);
              }
              return true;
            }
          }
          registerProcessor('pcm-processor', PcmProcessor);
        `;
        const blob = new Blob([workletCode], { type: "application/javascript" });
        const blobUrl = URL.createObjectURL(blob);
        await ctx.audioWorklet.addModule(blobUrl);
        URL.revokeObjectURL(blobUrl);

        const workletNode = new AudioWorkletNode(ctx, "pcm-processor");
        workletNode.port.onmessage = (ev: MessageEvent<Float32Array>) => {
          sendAudioToBoth(ev.data);
        };
        source.connect(workletNode);
        workletNode.connect(ctx.destination);
        workletNodeRef.current = workletNode;
      } catch {
        const processor = ctx.createScriptProcessor(CHUNK_SAMPLES, 1, 1);
        processor.onaudioprocess = (ev) => {
          sendAudioToBoth(ev.inputBuffer.getChannelData(0));
        };
        source.connect(processor);
        processor.connect(ctx.destination);
        workletNodeRef.current = processor;
      }

      setMicOn(true);
      setStatus("listening");
    } catch (err) {
      setError(`Mic access denied: ${err}`);
    }
  }, []);

  // Auto-start mic when both panels are connected
  useEffect(() => {
    if (status === "connected" && !micOn) {
      startMic();
    }
  }, [status]);

  const toggleMic = useCallback(() => {
    if (micOn) {
      stopMic();
    } else {
      startMic();
    }
  }, [micOn, startMic, stopMic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      panelARef.current.ws?.close();
      panelBRef.current.ws?.close();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
    };
  }, []);

  // --- Render ---

  const isConnected = status === "connected" || status === "listening";

  const statusColor = {
    disconnected: "bg-gray-400",
    connecting: "bg-yellow-400",
    connected: "bg-green-400",
    listening: "bg-green-400 animate-pulse",
  }[status];

  if (loading) {
    return (
      <PageLayout
        title="Transcribe"
        description="Live microphone transcription — side-by-side model comparison"
      >
        <View className="flex items-center justify-center p-12">
          <Text className="text-sm text-gray-500">Loading STT providers...</Text>
        </View>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Transcribe"
      description="Live microphone transcription — side-by-side model comparison"
    >
      <View className="flex flex-col gap-4 mt-6">
        {/* Top bar: per-panel config + connect */}
        <View className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
          <PanelConfigUI
            side="A"
            config={configA}
            onChange={(patch) => setConfigA((prev) => ({ ...prev, ...patch }))}
            allModels={allModels}
            getModelByValue={getModelByValue}
            getProviderForModel={getProviderForModel}
            disabled={isConnected}
          />
          <PanelConfigUI
            side="B"
            config={configB}
            onChange={(patch) => setConfigB((prev) => ({ ...prev, ...patch }))}
            allModels={allModels}
            getModelByValue={getModelByValue}
            getProviderForModel={getProviderForModel}
            disabled={isConnected}
          />
          <Button
            variant={isConnected ? "outline" : "brand"}
            onClick={isConnected ? disconnect : connect}
            disabled={status === "connecting"}
          >
            {isConnected ? "Disconnect" : status === "connecting" ? "Connecting..." : "Connect"}
          </Button>
        </View>

        {error && (
          <View className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <Icon
              name="error"
              size={16}
              className="text-red-500"
            />
            <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
          </View>
        )}

        {/* Side-by-side event panels */}
        <View className="flex items-center justify-end mb-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showPartials}
              onChange={(e) => setShowPartials(e.target.checked)}
              className="accent-cyan-500 w-4 h-4"
            />
            <Text className="text-xs text-gray-500 dark:text-gray-400">Show partial transcriptions</Text>
          </label>
        </View>
        <View className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TranscribePanel
            label={`A: ${configA.modelValue}`}
            events={eventsA}
            avgLatencyMs={avgLatencyA}
            sampleCount={panelARef.current.latencySamples.length}
            showPartials={showPartials}
          />
          <TranscribePanel
            label={`B: ${configB.modelValue}`}
            events={eventsB}
            avgLatencyMs={avgLatencyB}
            sampleCount={panelBRef.current.latencySamples.length}
            showPartials={showPartials}
          />
        </View>

        {/* Bottom bar: mic + status */}
        <View className="flex items-center gap-4">
          <Button
            variant={micOn ? "brand" : "outline"}
            onClick={toggleMic}
            disabled={!isConnected}
          >
            <Icon
              name={micOn ? "mic" : "mic-off"}
              size={18}
              className="mr-1"
            />
            {micOn ? "Mic On" : "Mic Off"}
          </Button>

          <View className="flex items-center gap-2">
            <View className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
            <Text className="text-sm text-gray-600 dark:text-gray-400 capitalize">{status}</Text>
          </View>
        </View>
      </View>
    </PageLayout>
  );
};

// --- Event row ---

function EventRow({ event }: { event: TranscribeEvent }) {
  const time = event.timestamp.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);

  const isPartial = event.type === "transcription.partial";
  const isFinal = event.type === "transcription.completed";
  const isSpeech = event.type === "speech.started" || event.type === "speech.stopped";
  const isError = event.type === "error";
  const isSession = event.type.startsWith("session.");

  let typeColor = "text-gray-500 dark:text-gray-400";
  if (isFinal) typeColor = "text-green-600 dark:text-green-400";
  else if (isPartial) typeColor = "text-gray-400 dark:text-gray-500";
  else if (isSpeech) typeColor = "text-blue-500 dark:text-blue-400";
  else if (isError) typeColor = "text-red-500 dark:text-red-400";
  else if (isSession) typeColor = "text-purple-500 dark:text-purple-400";

  return (
    <View className="flex gap-2 py-0.5 leading-5">
      <Text className="text-gray-400 dark:text-gray-600 shrink-0 tabular-nums">{time}</Text>
      <Text className={`shrink-0 w-48 truncate ${typeColor}`}>{event.type}</Text>
      {event.text && <Text className={isPartial ? "text-gray-400 dark:text-gray-500 italic" : "text-gray-900 dark:text-gray-100"}>{event.text}</Text>}
      {event.latencyMs !== undefined && <Text className="text-yellow-600 dark:text-yellow-400 shrink-0 ml-auto">({event.latencyMs}ms)</Text>}
      {isError && <Text className="text-red-600 dark:text-red-400">{(event.raw.error as Record<string, unknown>)?.message as string}</Text>}
    </View>
  );
}
